import { supabase } from './supabaseClient';
import * as fmt from '../i18n/format';
import type { Plan, SubscriptionWithPlan, Payment, PlanLimits } from '../types';

/**
 * Busca todos os planos ativos
 */
export async function getPlans(): Promise<Plan[]> {
  const { data, error } = await supabase
    .from('plans')
    .select('*')
    .eq('is_active', true)
    .order('sort_order');

  if (error) throw error;
  return data as Plan[];
}

/**
 * Busca um plano por nome
 */
export async function getPlanByName(name: string): Promise<Plan> {
  const { data, error } = await supabase
    .from('plans')
    .select('*')
    .eq('name', name)
    .single();

  if (error) throw error;
  return data as Plan;
}

/**
 * Busca a assinatura do usuário atual
 */
export async function getUserSubscription(userId: string): Promise<SubscriptionWithPlan | null> {
  const { data, error } = await supabase
    .from('subscriptions')
    .select(`
      *,
      plan:plans(*)
    `)
    .eq('user_id', userId)
    .single();

  if (error && error.code !== 'PGRST116') { // PGRST116 = no rows found
    throw error;
  }
  return data as SubscriptionWithPlan | null;
}

/**
 * Busca o histórico de pagamentos do usuário
 */
export async function getUserPayments(userId: string, limit: number = 10): Promise<Payment[]> {
  const { data, error } = await supabase
    .from('payments')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: false })
    .limit(limit);

  if (error) throw error;
  return data as Payment[];
}

/**
 * Conta quantos projetos o usuário possui (como owner)
 */
export async function countUserProjects(userId: string): Promise<number> {
  const { count, error } = await supabase
    .from('project_members')
    .select('*', { count: 'exact', head: true })
    .eq('user_id', userId)
    .eq('role', 'owner');

  if (error) throw error;
  return count || 0;
}

/**
 * Verifica se o usuário pode criar um novo projeto
 */
export async function canCreateProject(userId: string): Promise<boolean> {
  const subscription = await getUserSubscription(userId);
  if (!subscription?.plan) return false;

  const limit = subscription.plan.limits?.projects;

  // -1 significa ilimitado
  if (limit === -1) return true;

  const currentCount = await countUserProjects(userId);
  return currentCount < limit;
}

/**
 * Verifica se o usuário tem uma feature específica
 */
export async function hasFeature(userId: string, feature: string): Promise<boolean> {
  const subscription = await getUserSubscription(userId);
  if (!subscription?.plan) return false;

  const features: string[] = subscription.plan.features || [];
  return features.includes(feature);
}

/**
 * Formata preço de centavos para reais
 */
export function formatPrice(cents: number): string {
  if (cents === 0) return 'Grátis';
  return fmt.formatCurrency(cents / 100);
}

/**
 * Formata preço mensal a partir do preço anual
 */
export function formatYearlyAsMonthly(yearlyCents: number): string {
  const monthly = yearlyCents / 12;
  return fmt.formatCurrency(monthly / 100);
}

/**
 * Calcula economia do plano anual vs mensal
 */
export function calculateYearlySavings(monthlyPrice: number, yearlyPrice: number): { savings: number; percentage: number } {
  const yearlyIfMonthly = monthlyPrice * 12;
  const savings = yearlyIfMonthly - yearlyPrice;
  const percentage = Math.round((savings / yearlyIfMonthly) * 100);
  return { savings, percentage };
}
